import React, { useState, useEffect } from 'react';
import JqueryLoad from '../../../JqueryLoad/JqueryLoad';
import Hdashboard from '../../../components/Hdashboard';
import { useNavigate } from 'react-router-dom';
import { subscription_history_list } from '../../../api/apiHandler';
import DataTable from 'react-data-table-component';
import customStyles from '../../../utils/dataTableStyles';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import useDebounce from '../../../utils/useDebounce';
import { formatAmount } from '../../../utils/formatMoney';
import { WEB_URL } from '../../../Config';
import { encryptString, decryptString } from '../../../api/apiClient';
import { TOAST_ERROR } from '../../../utils/common.service';

// Every billing event of BOTH subscription products — New Leads (teachers) and
// AI ScholarTutor — from the append-only tbl_subscription_history log. See
// admin_model.subscription_history_list.
//
// The cards total the money actually collected under the same filters as the
// table (settled payments only), so picking a Start / End Date answers "how
// much was paid between these dates". They are split by currency rather than
// converted, since the AI plans can be priced in CAD while New Leads bills USD.

const emptyBucket = { USD: 0, CAD: 0, payments: 0 };
const emptySummary = { all: emptyBucket, new_leads: emptyBucket, ai_scholar_tutor: emptyBucket };

const formatDate = (value, withTime = false) => {
  if (!value || String(value).startsWith('0000')) return '-';
  const m = moment(value, 'YYYY-MM-DD HH:mm:ss');
  return m.isValid() ? m.format(withTime ? 'DD/MM/YYYY hh:mm A' : 'DD/MM/YYYY') : '-';
};

const eventColors = {
  'Payment Failed': '#dc3545',
  'Cancelled': '#6c757d',
  'Expired': '#6c757d',
};

export default function SubscriptionHistory({ isLoader }) {

  const [historyData, setHistoryData] = useState([]);
  const [summary, setSummary] = useState(emptySummary);
  const [product, setProduct] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [search, setSearch] = useState('');
  const debounceSearch = useDebounce(search, 750);

  const navigate = useNavigate();

  // Same rule as the Earnings page: the date range only applies once both ends
  // are picked, so the list does not refetch on a half-filled range.
  const hasRange = Boolean(startDate && endDate);

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, debounceSearch, hasRange ? startDate : '', hasRange ? endDate : '']);

  const fetchHistory = () => {
    const requestData = { search };
    if (product !== 'all') requestData.product = product;
    if (hasRange) {
      requestData.start_date = startDate;
      requestData.end_date = endDate;
    }

    isLoader(true);
    subscription_history_list(requestData)
      .then((response) => {
        if (response.code === "1" && response.data) {
          setHistoryData((response.data.list || []).map((row, index) => ({ ...row, count: index + 1 })));
          setSummary({ ...emptySummary, ...(response.data.summary || {}) });
        } else {
          setHistoryData([]);
          setSummary(emptySummary);
        }
        isLoader(false);
      })
      .catch(() => {
        setHistoryData([]);
        setSummary(emptySummary);
        isLoader(false);
      });
  };

  const clearDates = () => {
    setStartDate('');
    setEndDate('');
  };

  const AdminId = localStorage.getItem("aID");

  const handleUserAdminLogin = (row) => {
    try {
      const userPassword = decryptString(row?.password);

      const adminLoginPayload = {
        email: row?.email,
        password: userPassword,
        is_admin_login: 1,
        user_id: row?.id,
        category: row?.category,
        admin_id: AdminId,
        login_type: (row?.login_type != undefined && row?.login_type != null && row?.login_type != '') ? row?.login_type : "S",
        social_id: row?.social_id,
      };

      const encryptedPayload = encryptString(JSON.stringify(adminLoginPayload));
      const url = `${WEB_URL}login?admin_login=${encryptedPayload}`;

      const adminLoginWindow = window.open(url, 'studentscholars_admin_login');
      if (adminLoginWindow) {
        adminLoginWindow.focus();
      }
    } catch (error) {
      console.log('handleUserAdminLogin error :', error);
      TOAST_ERROR('ERROR..!!');
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const columns = [
    {
      name: 'No',
      selector: row => row.count,
      sortable: false,
      width: "70px"
    },
    {
      name: 'Date',
      selector: row => row.event_date,
      format: row => formatDate(row.event_date, true),
      sortable: true,
      width: "180px"
    },
    {
      name: 'Subscription',
      selector: row => row.product_label,
      sortable: true,
      width: "160px"
    },
    {
      name: 'User',
      selector: row => row.full_name || '-',
      cell: row => (
        <div className="py-2">
          <div
            className="fw-500"
            onClick={() => handleUserAdminLogin(row)}
            style={{ cursor: 'pointer', color: 'rgb(87, 8, 97)' }}
          >
            {row.full_name || '-'}
          </div>
          {row.email && <div className="text-muted" style={{ fontSize: '12px' }}>{row.email}</div>}
        </div>
      ),
      sortable: true,
      wrap: true,
      width: "230px"
    },
    {
      name: 'User Type',
      selector: row => row.category ? row.category.charAt(0).toUpperCase() + row.category.slice(1) : '-',
      sortable: true,
      width: "130px"
    },
    {
      name: 'Plan',
      selector: row => row.plan_name || '-',
      sortable: true,
      wrap: true,
      width: "170px"
    },
    {
      name: 'Billing',
      selector: row => row.billing_interval ? row.billing_interval.charAt(0).toUpperCase() + row.billing_interval.slice(1) : '-',
      sortable: true,
      width: "110px"
    },
    {
      name: 'Event',
      selector: row => row.event_label,
      cell: row => (
        <span className="fw-500" style={{ color: eventColors[row.event_label] || '#198754' }}>
          {row.event_label}
        </span>
      ),
      sortable: true,
      width: "170px"
    },
    {
      name: 'Amount',
      selector: row => Number(row.amount) || 0,
      format: row => `${row.currency === 'CAD' ? 'CAD$' : 'US$'} ${formatAmount(row.amount)}`,
      sortable: true,
      width: "130px"
    },
    {
      name: 'Billing Period',
      selector: row => row.period_start || '',
      format: row => row.period_start || row.period_end
        ? `${formatDate(row.period_start)} - ${formatDate(row.period_end)}`
        : '-',
      sortable: true,
      wrap: true,
      width: "210px"
    },
    {
      name: 'Invoice',
      cell: row => row.hosted_invoice_url || row.invoice_pdf_url ? (
        <a
          href={row.hosted_invoice_url || row.invoice_pdf_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-purple fw-500"
        >
          View
        </a>
      ) : '-',
      width: "100px"
    },
  ];

  const renderCard = (title, bucket) => (
    <div className='col-lg-2 col-md-3 mt-3 p-4 card-container'>
      <div className='content card-content'>
        <p className='fs-5 fw-600'>{title}</p>
        <p className='fs-6 fw-500 mt-3'>USD Total: US$ {formatAmount(bucket.USD)}</p>
        {/* <p className='fs-6 fw-500'>CAD Total: CAD$ {formatAmount(bucket.CAD)}</p> */}
        <p className='fs-6 fw-600 mt-2'>Payments : {bucket.payments || 0}</p>
      </div>
    </div>
  );

  return (
    <>
      <JqueryLoad />
      <Hdashboard />
      <Helmet>
        <style>
          {`
            .card-container {
              border: 1px solid #e1dfdf;
              box-shadow: rgba(0, 0, 0, 0.20) 0px 2px 5px;
              border-radius: 10px;
              background-color: #570861;
              color: white;
              transition: transform 0.3s ease, box-shadow 0.3s ease;
            }

            .card-container:hover {
              transform: translateY(-5px);
              box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
            }

            .card-content {
              transition: color 0.3s ease;
            }

            .card-container:hover .card-content {
              color: #ffffffcc;
            }

            /* Keep the wide table from scrolling the whole document under the
               fixed sidebar — same fix as the Earnings / E-Wallet pages. */
            .dashboard-app {
              overflow-x: hidden;
              max-width: 100%;
            }

            #subscription_history_table {
              overflow-x: auto;
              max-width: 100%;
              border-radius: 8px;
              border: 1px solid #e1dfdf;
            }
          `}
        </style>
      </Helmet>
      <main>
        <div className="dashboard-app d-lg-flex px-5 mt-1">
          <header className="dashboard-toolbar d-lg-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <a className="menu-toggle align-items-center d-lg-none d-flex">
                <i className="fa fa-bars" />
              </a>
              <a href="index.php" className="d-lg-none d-block brand-logo">
                <img src="./assets/images/logo.png" alt="logo" />
              </a>
            </div>
          </header>
          <span onClick={handleBack} className="mt-3">
            <svg width="10" height="18" viewBox="0 0 10 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.86172 2.225L8.37839 0.75L0.136719 9L8.38672 17.25L9.86172 15.775L3.08672 9L9.86172 2.225Z" fill="#131313" ></path>
            </svg>
            <span className="ms-2">Back</span>
          </span>

          <div className="d-flex justify-content-between title mt-2 mb-2">
            <p className="fs-3 fw-500">Subscription History</p>
          </div>

          <div className='d-flex flex-wrap gap-3'>
            {renderCard('Total Payments', summary.all)}
            {renderCard('New Leads', summary.new_leads)}
            {renderCard('AI ScholarTutor', summary.ai_scholar_tutor)}
          </div>

          <div>
            <div className="d-flex flex-wrap align-items-end mt-3 gap-3">
              <div className="col-md-2">
                <label htmlFor="history-start-date" className="mb-2">Start Date:</label>
                <input
                  type="date"
                  name="start_date"
                  id="history-start-date"
                  className="form-control"
                  value={startDate}
                  max={endDate || undefined}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="col-md-2">
                <label htmlFor="history-end-date" className="mb-2">End Date:</label>
                <input
                  type="date"
                  name="end_date"
                  id="history-end-date"
                  className="form-control"
                  value={endDate}
                  min={startDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>

              <div className="col-md-2">
                <label htmlFor="product-select" className="mb-2">Subscription:</label>
                <select
                  id="product-select"
                  className="form-control"
                  value={product}
                  onChange={(e) => setProduct(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="new_leads">New Leads</option>
                  <option value="ai_scholar_tutor">AI ScholarTutor</option>
                </select>
              </div>

              <div className="col-md-3">
                <label htmlFor="history-search" className="mb-2">Search:</label>
                <input
                  type="text"
                  id="history-search"
                  className="form-control"
                  placeholder="Search name, email, plan, invoice..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {(startDate || endDate) &&
                <div className="d-flex align-items-end flex-shrink-0">
                  <button className="btn-theme bg-yellow" onClick={clearDates}>Clear Dates</button>
                </div>
              }
            </div>

            <div id='subscription_history_table'>
              <div className='mt-3'>
                {historyData && historyData.length > 0 ? (
                  <DataTable columns={columns} data={historyData} keyField="id" pagination customStyles={customStyles} />
                ) : (
                  <p className="mt-5 text-center text-purple fw-500 fs-3">No data Found</p>
                )}
              </div>
            </div>
          </div>

        </div>
      </main>
    </>
  );
}
